import { SafeError, SafeType } from '@benzinga/safe-await';
import {
  TradeIdea,
  TradeIdeaArticleNode,
  TradeIdeaArticleNodeJSON,
  TradeIdeaComment,
  TradeIdeaCommentJSON,
  TradeIdeaJSON,
  TradeIdeaResponse,
  TradeIdeaTips,
  TradeIdeaTipsJSON,
  Trader,
  TraderJSON,
} from './entities';

export const ingressTrader = (trader: TraderJSON): Trader => ({
  benzingaUID: trader.benzinga_uid,
  createdAt: trader.created_at,
  id: trader.id,
  name: trader.name,
  numTrades: trader.num_trades,
  profilePicture: trader.profile_picture,
  status: trader.status,
  tradeIdeas: trader.tradeIdeas ? trader.tradeIdeas.map(idea => ingressIdea(idea)) : null,
  updatedAt: trader.updated_at,
  verified: trader.verified,
});

export const ingressTradeIdeaComment = (comment: TradeIdeaCommentJSON): TradeIdeaComment => ({
  benzingaUID: comment.benzinga_uid,
  createdAt: comment.created_at,
  id: comment.id,
  text: comment.text,
  tradeIdeaId: comment.trade_idea_id,
  trader: ingressTrader(comment.trader),
  traderId: comment.trader_id,
  updatedAt: comment.updated_at,
});

export const ingressTradeIdeaTips = (tip: TradeIdeaTipsJSON): TradeIdeaTips => ({
  amount: tip.amount,
  benzingaUID: tip.benzinga_uid,
  createdAt: tip.created_at,
  id: tip.id,
  modelId: tip.model_id,
  modelType: tip.model_type,
  tipperId: tip.tipper_id,
  updatedAt: tip.updated_at,
});

export const ingressArticleNode = (articleNode: TradeIdeaArticleNodeJSON): TradeIdeaArticleNode => ({
  author: articleNode.author ?? null,
  authorId: articleNode.author_id ?? null,
  channels: articleNode.channels ?? null,
  created: articleNode.created ?? null,
  id: articleNode.id ?? null,
  tags: articleNode.tags ?? null,
  teaser: articleNode.teaser ?? null,
  thumbnail: articleNode.thumbnail ?? null,
  title: articleNode.title ?? null,
  updated: articleNode.updated ?? null,
});

export const ingressIdea = (tradeIdea: TradeIdeaJSON, comments?: TradeIdeaCommentJSON[]): TradeIdea => ({
  action: tradeIdea.action,
  allocation: tradeIdea.allocation,
  avatarProps: {
    id: tradeIdea.trader?.id,
    name: tradeIdea.trader?.name,
    photo: tradeIdea.trader?.profile_picture,
  },
  comments: tradeIdea.comments
    ? tradeIdea.comments.map(ingressTradeIdeaComment)
    : comments
      ? comments.filter(comment => comment.trade_idea_id == tradeIdea.id).map(ingressTradeIdeaComment)
      : [],
  companyName: tradeIdea.company_name,
  contributor: tradeIdea.contributor,
  contributorId: tradeIdea.contributor_id,
  createdAt: tradeIdea.created_at,
  downvotes: tradeIdea.downvotes,
  duration: tradeIdea.duration ?? null,
  durationText: tradeIdea.duration_text ?? null,
  financialAssetPositionProps: {
    direction: tradeIdea.position,
  },
  id: tradeIdea.id,
  modelId: tradeIdea.model_id,
  modelType: tradeIdea.model_type,
  multiplier: tradeIdea.multiplier,
  node: tradeIdea.node ? ingressArticleNode(tradeIdea.node) : null,
  numComments: tradeIdea.num_comments,
  numZing: tradeIdea.num_zing,
  position: tradeIdea.position,
  price: tradeIdea.price,
  reason: tradeIdea.reason,
  reviewedAt: tradeIdea.reviewed_at,
  reviewerId: tradeIdea.reviewer_id,
  risk: tradeIdea.risk ?? null,
  status: tradeIdea.status,
  tags: tradeIdea.tags,
  tickerProps: {
    symbol: tradeIdea.tickers,
  },
  tickers: tradeIdea.tickers ?? '',
  tips: tradeIdea?.tips ? tradeIdea?.tips?.map(tip => ingressTradeIdeaTips(tip)) : [],
  trader: ingressTrader(tradeIdea.trader),
  traderId: tradeIdea.trader_id,
  updatedAt: tradeIdea.updated_at,
  upvotes: tradeIdea.upvotes,
});
export interface IngressTradeIdeaResponse {
  comments: TradeIdeaCommentJSON[];
  data: TradeIdeaJSON[];
  tips: TradeIdeaTipsJSON[];
}

export const ingressFind = (tradeIdeas: SafeType<IngressTradeIdeaResponse>): SafeType<TradeIdeaResponse> => {
  if (tradeIdeas.err) {
    return tradeIdeas as unknown as SafeType<TradeIdeaResponse>;
  } else if (tradeIdeas.ok) {
    const comments = tradeIdeas.ok.comments ?? [];
    return {
      ok: {
        comments: comments?.map(c => ingressTradeIdeaComment(c)) ?? [],
        data: tradeIdeas.ok?.data
          .filter((tradeIdea: TradeIdeaJSON) => Boolean(tradeIdea.trader))
          .map((tradeIdea: TradeIdeaJSON) => ingressIdea(tradeIdea, comments)),
      },
    };
  } else {
    return { err: new SafeError('could not ingress Find', 'trade_idea_ingress_find') };
  }
};

export const ingressTradeIdeaIndividual = (tradeIdeas: SafeType<{ data: TradeIdeaJSON }>): SafeType<TradeIdea> => {
  if (tradeIdeas.err) {
    return tradeIdeas as unknown as SafeType<TradeIdea>;
  } else if (tradeIdeas.ok) {
    return {
      ok: ingressIdea(tradeIdeas.ok?.data),
    };
  } else {
    return { err: new SafeError('could not ingress Find', 'trade_idea_ingress_find') };
  }
};
